// @ts-nocheck
import React, { useState, useEffect } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAnalytics } from '../../context/AnalyticsContext';
import SetupInstructions from './SetupInstructions';
import OAuthFlowTracker from './OAuthFlowTracker';
import { 
  Settings, 
  ExternalLink, 
  CheckCircle, 
  AlertCircle, 
  ArrowLeft, 
  ArrowRight,
  RefreshCw,
  Database,
  Key,
  Globe,
  Building,
  BookOpen,
  Play
} from 'lucide-react';

interface ProviderSetupWizardProps {
  provider: 'zoho' | 'powerbi';
  onComplete?: () => void;
  onCancel?: () => void;
}

type SetupStep = 'instructions' | 'configuration' | 'authorization' | 'schema' | 'complete';

const ProviderSetupWizard: React.FC<ProviderSetupWizardProps> = ({ 
  provider, 
  onComplete, 
  onCancel 
}) => {
  const { state, actions } = useAnalytics();
  const [currentStep, setCurrentStep] = useState<SetupStep>('instructions');
  const [config, setConfig] = useState({
    // Zoho specific
    clientId: '',
    clientSecret: '',
    orgIdOrEmail: '',
    workspace: '',
    defaultRegion: 'com' as const,
    
    // Power BI specific
    aadTenantId: '',
    groupId: '',
    datasetId: '',
    
    // Common
    defaultTable: '',
    defaultSearchColumn: '',
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps = [
    { id: 'instructions' as const, label: 'Setup Guide', icon: BookOpen },
    { id: 'configuration' as const, label: 'Configuration', icon: Settings },
    { id: 'authorization' as const, label: 'Authorization', icon: Key },
    { id: 'schema' as const, label: 'Schema Discovery', icon: Database },
    { id: 'complete' as const, label: 'Complete', icon: CheckCircle }
  ];

  const providerState = state.providers[provider];

  // Auto-advance based on provider state
  useEffect(() => {
    if (providerState.configured && currentStep === 'configuration') {
      setCurrentStep('authorization');
    }
    if (providerState.authorized && currentStep === 'authorization') {
      setCurrentStep('schema');
    }
    if (state.schema[provider]?.data && currentStep === 'schema') {
      setCurrentStep('complete');
    }
  }, [providerState, state.schema[provider], currentStep]);

  const handleConfigSave = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Validate required fields
      if (provider === 'zoho') {
        if (!config.clientId || !config.clientSecret || !config.orgIdOrEmail || !config.workspace) {
          throw new Error('Client ID, Client Secret, Organization ID/Email, and Workspace are required.');
        }
      } else if (provider === 'powerbi') {
        if (!config.clientId || !config.clientSecret || !config.aadTenantId) {
          throw new Error('Client ID, Client Secret, and Azure AD Tenant ID are required.');
        }
      }

      // Update analytics configuration - following API spec exactly
      await actions.updateConfiguration({
        [provider]: {
          enabled: true,
          clientId: config.clientId,
          clientSecret: config.clientSecret,
          ...(provider === 'zoho' && {
            defaultScope: 'ZohoAnalytics.metadata.read,ZohoAnalytics.data.read,ZohoAnalytics.modeling.read',
            defaultRegion: config.defaultRegion,
          }),
          ...(provider === 'powerbi' && {
            aadTenantId: config.aadTenantId,
            defaultScope: 'https://analysis.windows.net/powerbi/api/.default',
          }),
        },
        defaultProvider: provider,
      } as any);

      setCurrentStep('authorization');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartOAuth = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const successUrl = `${window.location.origin}/analytics?success=true`;
      const failureUrl = `${window.location.origin}/analytics?error=true`;
      
      if (provider === 'zoho') {
        await actions.connectProvider('zoho', {
          tenantId: 'default',
          region: config.defaultRegion,
          scope: 'ZohoAnalytics.metadata.read,ZohoAnalytics.data.read,ZohoAnalytics.modeling.read',
          successRedirectUrl: successUrl,
          failureRedirectUrl: failureUrl,
          orgIdOrEmail: config.orgIdOrEmail,
          workspace: config.workspace,
          defaultTable: config.defaultTable,
          defaultSearchColumn: config.defaultSearchColumn,
        });
      } else if (provider === 'powerbi') {
        await actions.connectProvider('powerbi', {
          tenantId: 'default',
          aadTenantId: config.aadTenantId,
          scope: 'https://analysis.windows.net/powerbi/api/.default',
          successRedirectUrl: successUrl,
          failureRedirectUrl: failureUrl,
          groupId: config.groupId,
          datasetId: config.datasetId,
          defaultTable: config.defaultTable,
          defaultSearchColumn: config.defaultSearchColumn,
        });
      }
    } catch (error: any) {
      setError(error.message);
      setIsLoading(false);
    }
  };

  const handleDiscoverSchema = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await actions.discoverSchema(provider, {
        maxTables: 20,
        includeMetadata: true,
      });
      
      setCurrentStep('complete');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getStepStatus = (stepId: SetupStep) => {
    const stepIndex = steps.findIndex(s => s.id === stepId);
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    
    if (stepIndex < currentIndex) return 'complete';
    if (stepIndex === currentIndex) return 'current';
    return 'pending';
  };

const renderStepIndicator = () => (
  <div className="mb-8 px-4">
    {/* Row 1: circles + connectors */}
    <div className="flex items-center">
      {steps.map((step, index) => {
        const status = getStepStatus(step.id);
        const IconComponent = step.icon;

        return (
          <React.Fragment key={step.id}>
            <div className="h-12 flex items-center justify-center">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                  status === 'complete'
                    ? 'bg-green-500 border-green-500 text-white shadow-lg'
                    : status === 'current'
                    ? 'bg-blue-500 border-blue-500 text-white shadow-lg'
                    : 'bg-white border-gray-300 text-gray-400'
                }`}
              >
                {status === 'complete' ? (
                  <CheckCircle className="w-6 h-6" />
                ) : (
                  <IconComponent className="w-6 h-6" />
                )}
              </div>
            </div>

            {/* Connector */}
            {index < steps.length - 1 && (
              <div
                className={`flex-1 h-1 rounded-full mx-6 self-center transition-all duration-300 ${
                  status === 'complete' ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>

    {/* Row 2: labels */}
    <div className="mt-3 flex justify-between">
      {steps.map((step) => {
        const status = getStepStatus(step.id);
        return (
          <div key={step.id} className="w-12 text-center">
            <div
              className={`w-20 -ml-4 text-sm font-medium leading-tight ${
                status === 'current'
                  ? 'text-blue-600'
                  : status === 'complete'
                  ? 'text-green-600'
                  : 'text-gray-500'
              }`}
            >
              {step.label}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);


  const renderStepContent = () => {
    switch (currentStep) {
      case 'instructions':
        return renderInstructionsStep();
      case 'configuration':
        return renderConfigurationStep();
      case 'authorization':
        return renderAuthorizationStep();
      case 'schema':
        return renderSchemaStep();
      case 'complete':
        return renderCompleteStep();
      default:
        return null;
    }
  };

  const renderInstructionsStep = () => (
    <div className="space-y-6">
      <SetupInstructions 
        provider={provider}
        onComplete={() => setCurrentStep('configuration')}
      />
      <div className="flex justify-end">
        <Button onClick={() => setCurrentStep('configuration')}>
          <Play className="w-4 h-4 mr-2" />
          Start Configuration
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderConfigurationStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          {provider === 'zoho' ? 'Zoho Analytics Configuration' : 'Power BI Configuration'}
        </h3>
        <p className="text-text-secondary">
          {provider === 'zoho' 
            ? 'Enter your Zoho OAuth application credentials and workspace details.'
            : 'Enter your Azure AD application credentials and Power BI details.'
          }
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Client ID *
          </label>
          <Input
            type="text"
            value={config.clientId}
            onChange={(e) => setConfig(prev => ({ ...prev, clientId: e.target.value }))}
            placeholder={`Enter your ${provider === 'zoho' ? 'Zoho' : 'Azure AD'} Client ID`}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Client Secret *
          </label>
          <Input
            type="password"
            value={config.clientSecret}
            onChange={(e) => setConfig(prev => ({ ...prev, clientSecret: e.target.value }))}
            placeholder={`Enter your ${provider === 'zoho' ? 'Zoho' : 'Azure AD'} Client Secret`}
          />
        </div>

        {provider === 'zoho' && (
          <>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Organization ID/Email *
              </label>
              <Input
                type="text"
                value={config.orgIdOrEmail}
                onChange={(e) => setConfig(prev => ({ ...prev, orgIdOrEmail: e.target.value }))}
                placeholder="Org ID or user@example.com"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Workspace Name *
              </label>
              <Input
                type="text"
                value={config.workspace}
                onChange={(e) => setConfig(prev => ({ ...prev, workspace: e.target.value }))}
                placeholder="Your Zoho Analytics workspace"
              />
            </div>
            
            {/* Region selector */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Region
              </label>
              <select
                className="w-full rounded border px-3 py-2 bg-white text-text-primary border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={config.defaultRegion}
                onChange={(e) => setConfig(prev => ({ ...prev, defaultRegion: e.target.value as any }))}
              >
                <option value="com">United States (.com)</option>
                <option value="eu">Europe (.eu)</option>
                <option value="in">India (.in)</option>
                <option value="com.au">Australia (.com.au)</option>
                <option value="jp">Japan (.jp)</option>
              </select>
              <p className="text-xs text-text-secondary mt-1">
                Choose the Zoho data center where your account resides. For EU accounts, pick Europe (.eu).
              </p>
            </div>
          </>
        )}

        {provider === 'powerbi' && (
          <>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Azure AD Tenant ID *
              </label>
              <Input
                type="text"
                value={config.aadTenantId}
                onChange={(e) => setConfig(prev => ({ ...prev, aadTenantId: e.target.value }))}
                placeholder="12345678-1234-1234-1234-123456789012"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Workspace (Group) ID
              </label>
              <Input
                type="text"
                value={config.groupId}
                onChange={(e) => setConfig(prev => ({ ...prev, groupId: e.target.value }))}
                placeholder="Power BI workspace ID (optional)"
              />
            </div>
          </>
        )}

        {provider === 'zoho' && (
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Default Table
            </label>
            <Input
              type="text"
              value={config.defaultTable}
              onChange={(e) => setConfig(prev => ({ ...prev, defaultTable: e.target.value }))}
              placeholder="Default table name (optional)"
            />
          </div>
        )}

        {provider === 'powerbi' && (
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Dataset ID
            </label>
            <Input
              type="text"
              value={config.datasetId}
              onChange={(e) => setConfig(prev => ({ ...prev, datasetId: e.target.value }))}
              placeholder="Power BI dataset ID (optional)"
            />
          </div>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
            <span className="text-sm text-red-800">{error}</span>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <Button
          onClick={() => setCurrentStep('instructions')}
          variant="secondary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Instructions
        </Button>
        <Button onClick={handleConfigSave} disabled={isLoading}>
          {isLoading ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <ArrowRight className="w-4 h-4 mr-2" />
          )}
          Save & Continue
        </Button>
      </div>
    </div>
  );

  const renderAuthorizationStep = () => (
    <div className="space-y-6">
      <OAuthFlowTracker
        provider={provider}
        onRetry={handleStartOAuth}
        onConfigure={() => setCurrentStep('configuration')}
      />
      
      <Card className="p-6">
        <div className="text-center">
          <Key className="w-12 h-12 text-blue-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            Authorize {provider === 'zoho' ? 'Zoho Analytics' : 'Power BI'}
          </h3>
          <p className="text-text-secondary mb-6">
            Click the button below to start the OAuth authorization process.
          </p>
          
          <div className="space-y-4">
            <Button
              onClick={handleStartOAuth}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4 mr-2" />
              )}
              Authorize with {provider === 'zoho' ? 'Zoho' : 'Microsoft'}
            </Button>
            
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  <span className="text-sm text-red-800">{error}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="flex justify-between">
        <Button
          onClick={() => setCurrentStep('configuration')}
          variant="secondary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Configuration
        </Button>
      </div>
    </div>
  );

  const renderSchemaStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-2">Discover Schema</h3>
        <p className="text-text-secondary">
          Discover your data tables and structure to enable analytics conversations.
        </p>
      </div>

      <Card className="p-6">
        <div className="text-center">
          <Database className="w-12 h-12 text-green-600 mx-auto mb-4" />
          <h4 className="text-md font-semibold text-text-primary mb-2">
            Ready to Discover Your Data
          </h4>
          <p className="text-text-secondary mb-6">
            This process will scan your {provider === 'zoho' ? 'Zoho Analytics workspace' : 'Power BI datasets'} 
            to identify available tables and columns.
          </p>
          
          <Button
            onClick={handleDiscoverSchema}
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Database className="w-4 h-4 mr-2" />
            )}
            Discover Schema
          </Button>
        </div>
      </Card>

      <div className="flex justify-between">
        <Button
          onClick={() => setCurrentStep('authorization')}
          variant="secondary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Authorization
        </Button>
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-text-primary mb-2">
          Setup Complete!
        </h3>
        <p className="text-text-secondary">
          Your {provider === 'zoho' ? 'Zoho Analytics' : 'Power BI'} integration is now ready to use.
        </p>
      </div>

      <Card className="p-6 bg-green-50 border-green-200">
        <h4 className="font-semibold text-green-800 mb-3">What's Next?</h4>
        <div className="space-y-2 text-sm text-green-700">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4" />
            <span>Start conversations with your analytics data</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4" />
            <span>Explore your data tables and schema</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4" />
            <span>Create visualizations and insights</span>
          </div>
        </div>
      </Card>

      <div className="flex justify-center">
        <Button onClick={onComplete}>
          <CheckCircle className="w-4 h-4 mr-2" />
          Start Using Analytics
        </Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-text-primary">
          {provider === 'zoho' ? 'Zoho Analytics' : 'Power BI'} Setup
        </h2>
        <p className="text-text-secondary mt-1">
          Follow the steps below to connect your analytics provider.
        </p>
      </div>

      {renderStepIndicator()}
      {renderStepContent()}

      <div className="flex justify-between">
        <Button onClick={onCancel} variant="secondary">
          Cancel
        </Button>
        {currentStep !== 'instructions' && (
          <Button
            onClick={() => setCurrentStep('instructions')}
            variant="secondary"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            View Setup Guide
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProviderSetupWizard;